import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Crown, Sparkles, LogOut, Zap, ExternalLink } from 'lucide-react';
import { useRemainingGenerations, useDailyLimit } from '../abo/useLimits';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../firebase';
import { STRIPE_ENABLED, getSubscriptionsCollectionPath } from '../util/constants';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

const getDateFromTimestamp = (timestamp: any): Date => {
    if (!timestamp) return new Date(0);
    if (typeof timestamp === 'object' && 'seconds' in timestamp) {
        return new Date(timestamp.seconds * 1000);
    }
    const num = typeof timestamp === 'string' ? parseFloat(timestamp) : timestamp;
    if (isNaN(num)) return new Date(0);
    if (num > 1e12) {
        return new Date(num);
    } else {
        return new Date(num * 1000);
    }
};

const Profile = () => {
    const { currentUser, profile, logout, profileLoading } = useAuth();
    const navigate = useNavigate();
    const remaining = useRemainingGenerations();
    const dailyLimit = useDailyLimit();
    const [portalLoading, setPortalLoading] = useState(false);
    const [subscription, setSubscription] = useState<any>(null);

    useEffect(() => {
        if (!currentUser || currentUser.isAnonymous) return;
        const subsRef = collection(db, getSubscriptionsCollectionPath(currentUser.uid));
        const q = query(subsRef, where('status', 'in', ['active', 'trialing']));
        const unsub = onSnapshot(q, (snap) => {
            if (!snap.empty) {
                const doc = snap.docs[0];
                setSubscription({ id: doc.id, ...doc.data() });
            } else {
                setSubscription(null);
            }
        });
        return unsub;
    }, [currentUser]);

    if (currentUser?.isAnonymous) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-4 pt-24 flex items-center justify-center">
                <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 max-w-md text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">👤 Du bist Gast</h2>
                    <p className="text-gray-600 mb-4">
                        Deine Lernsets werden nur in diesem Browser gespeichert. Erstelle ein kostenloses Konto, um deine Daten zu sichern und später auf Pro upgraden zu können.
                    </p>
                    <Link to="/register" className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition">
                        Konto erstellen
                    </Link>
                </div>
            </div>
        );
    }

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const openCustomerPortal = async () => {
        setPortalLoading(true);
        try {
            const createPortalLink = httpsCallable(functions, 'createPortalLink');
            const result = await createPortalLink({
                returnUrl: window.location.origin + '/profil',
            });
            const url = (result.data as { url: string }).url;
            window.location.href = url;
        } catch (error: any) {
            console.error('Portal-Fehler:', error);
            alert(error.message || 'Fehler beim Öffnen des Portals. Bitte versuche es später erneut.');
        } finally {
            setPortalLoading(false);
        }
    };

    if (profileLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-4 pt-24">
            <div className="max-w-2xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/60 overflow-hidden"
                >
                    <div className="bg-gradient-to-br from-indigo-600 to-purple-600 px-8 pt-10 pb-12 text-center relative">
                        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white/50 text-white text-4xl font-bold mb-4">
                            {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-1">Mein Profil</h1>
                        <p className="text-indigo-100 text-sm">{currentUser?.email}</p>
                    </div>

                    <div className="p-8 space-y-6">
                        <div className="bg-white/50 rounded-2xl p-5 border border-gray-100">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Crown className="w-5 h-5 text-amber-500" />
                                    <span className="font-semibold text-gray-700">Aktueller Tarif</span>
                                </div>
                                <span className={`px-3 py-1 rounded-full text-sm font-bold ${profile?.tier === 'pro' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`}>
                                    {profile?.tier === 'pro' ? 'Pro ⚡' : 'Free'}
                                </span>
                            </div>

                            {profile?.tier === 'free' ? (
                                <>
                                    <p className="text-gray-600 text-sm mb-3">
                                        Tägliches Limit: <strong>{dailyLimit ?? 3}</strong> Generierungen<br />
                                        Heute noch <strong>{remaining ?? 0}</strong> übrig.
                                    </p>
                                    {STRIPE_ENABLED ? (
                                        <Link
                                            to="/upgrade"
                                            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                                        >
                                            <Zap className="w-4 h-4" />
                                            Upgrade auf Pro
                                        </Link>
                                    ) : (
                                        <span className="text-gray-400 text-sm">(Pro‑Upgrade bald verfügbar)</span>
                                    )}
                                </>
                            ) : (
                                STRIPE_ENABLED && (
                                    <div className="space-y-3">
                                        <p className="text-gray-600 text-sm">
                                            ✨ Du genießt unbegrenzte Generierungen und alle Pro-Features.
                                        </p>

                                        {subscription?.cancel_at_period_end && (
                                            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-sm text-yellow-800">
                                                ⚠️ Ihr Abo wurde gekündigt und läuft am{' '}
                                                {getDateFromTimestamp(subscription.current_period_end).toLocaleDateString('de-DE')} aus.
                                                Sie können die Kündigung jederzeit im Stripe‑Portal rückgängig machen.
                                            </div>
                                        )}

                                        <button
                                            onClick={openCustomerPortal}
                                            disabled={portalLoading}
                                            className="w-full py-2 bg-indigo-50 text-indigo-600 rounded-lg font-medium hover:bg-indigo-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                                        >
                                            {portalLoading ? (
                                                <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                                            ) : (
                                                <>
                                                    <ExternalLink className="w-4 h-4" />
                                                    Abo verwalten (kündigen, Zahlungsmethode)
                                                </>
                                            )}
                                        </button>
                                        <p className="text-xs text-gray-400 text-center">
                                            Du wirst zu Stripe weitergeleitet, um dein Abo sicher zu verwalten.
                                        </p>
                                    </div>
                                )
                            )}
                        </div>

                        <div className="bg-white/50 rounded-2xl p-5 border border-gray-100">
                            <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-indigo-500" />
                                Deine Lernaktivität
                            </h3>
                            <div className="grid grid-cols-2 gap-4 text-center">
                                <div>
                                    <p className="text-2xl font-bold text-indigo-600">{profile?.usage.generateCount ?? 0}</p>
                                    <p className="text-xs text-gray-500">Heutige Generierungen</p>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-indigo-600">{dailyLimit ?? 0}</p>
                                    <p className="text-xs text-gray-500">Tägliches Limit</p>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleLogout}
                            className="w-full py-3 flex items-center justify-center gap-2 bg-red-50 text-red-600 rounded-xl font-medium hover:bg-red-100 transition-colors"
                        >
                            <LogOut className="w-5 h-5" />
                            Abmelden
                        </button>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default Profile;